import { requestUpdateBilling } from "@/state/billing/BillingEffects";
import { requestSearchContactsForOffice } from "@/state/contact/ContactsEffects";
import { requestSearchMembersForOffice } from "@/state/offices/OfficesEffects";
import { ActiveUser } from "@/type/core/auth/auth.types";
import { Contact } from "@/type/core/dashboard/dashboard.types";
import {
  Billing,
  ContactSort,
  Office,
} from "@/type/core/offices/offices.types";
import { toastError, toastSuccess } from "@/utils/helpers/toast-helper";
import BasicButton from "@components/core/buttons/BasicButton";
import BasicLink from "@components/core/links/BasicLink";
import InfoModal from "@components/core/modals/InfoModal";
import Recipient from "@components/core/subComponents/Recipient";
import { ROUTES } from "@routes/Routes";
import { Col, Row } from "antd";
import { useForm } from "antd/es/form/Form";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import BillingForm from "@views/core/extranet/menu/offices/office/BillingForm";
import OfficeDescription from "@views/core/extranet/menu/offices/office/OfficeDescription";

interface OfficeCellProps {
  office: Office;
}

const OfficeCell: FunctionComponent<OfficeCellProps> = ({ office }) => {
  const officeId = office.id;
  const showIsExpertComptable = office.expertComptable;

  const { t } = useTranslation();
  const [billingForm] = useForm();

  const [fieldsChanged, setFieldsChanged] = useState<boolean>(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(false);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [members, setMembers] = useState<Contact[]>([]);
  const [tempEmailFacturationAddress, setTempEmailFacturationAddress] =
    useState<ContactSort[]>(office.billing?.emailFacturationAddress ?? []);

  const billingAddressRef = useRef<HTMLHeadingElement | null>(null);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash === `#billingAddress_${office.id}` && billingAddressRef.current) {
      billingAddressRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [office.id]);

  useEffect(() => {
    billingForm.setFields([
      {
        name: ["officeName"],
        value: office.billing?.officeName ?? office.name,
      },
      {
        name: ["address1"],
        value: office.billing?.address1,
      },
      {
        name: ["address2"],
        value: office.billing?.address2,
      },
      {
        name: ["zipCode"],
        value: office.billing?.zipCode,
      },
      {
        name: ["city"],
        value: office.billing?.city,
      },
    ]);
  }, [office]);

  useEffect(() => {
    void requestSearchContactsForOffice({ id: officeId });
    void requestSearchMembersForOffice({ id: officeId });
  }, [officeId]);

  useEffect(() => {
    return requestSearchContactsForOffice.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setContacts(result.data.contacts || []);
      } else {
        toastError(t("error.toast.contactLoading"));
      }
    });
  });

  useEffect(() => {
    return requestSearchMembersForOffice.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setMembers(result.data.contacts || []);
      } else {
        toastError(t("error.retrieveMembers"));
      }
    });
  });

  useEffect(() => {
    return requestUpdateBilling.done.watch(({ result }) => {
      if (result.ok && result.data) {
        toastSuccess(t("success.updateBilling"));
        office.billing = {
          officeId,
          officeName: billingForm.getFieldValue("officeName"),
          address1: billingForm.getFieldValue("address1"),
          address2: billingForm.getFieldValue("address2"),
          zipCode: billingForm.getFieldValue("zipCode"),
          city: billingForm.getFieldValue("city"),
          emailFacturationAddress: tempEmailFacturationAddress,
        };
        onFieldsChange();
      } else {
        toastError(t("error.toast.updateBilling"));
        setTempEmailFacturationAddress(
          office.billing?.emailFacturationAddress ?? [],
        );
      }
      setFieldsChanged(false);
    });
  });

  const handleBillingFormSubmit = (values: Billing) => {
    if (officeId) {
      handleUpdateBilling(tempEmailFacturationAddress, values);
    }
  };

  const handleResetForm = () => {
    billingForm.resetFields();
    if (office) {
      billingForm.setFieldsValue({
        officeName: office?.billing?.officeName,
        address1: office?.billing?.address1,
        address2: office?.billing?.address2,
        zipCode: office?.billing?.zipCode,
        city: office?.billing?.city,
      });
    }
    setFieldsChanged(false);
  };

  const onFieldsChange = () => {
    const fields = ["officeName", "address1", "address2", "zipCode", "city"];
    const requiredFields = ["officeName", "address1", "zipCode", "city"];
    const nonRequiredFields = fields.filter(
      (field) => !requiredFields.includes(field),
    );

    const valuesChanged = fields.some(
      (field) =>
        office.billing?.[field as keyof typeof office.billing] !==
        billingForm.getFieldValue(field),
    );

    const hasEmptyRequired = requiredFields.some(
      (field: string) =>
        !billingForm.getFieldValue(field) ||
        billingForm.getFieldValue(field) === "",
    );
    const isUnchangedRequired = requiredFields.every(
      (field: string) =>
        office.billing?.[field as keyof typeof office.billing] ===
          billingForm.getFieldValue(field) ||
        billingForm.getFieldValue(field) === "",
    );
    const isUnchangedNonRequired = nonRequiredFields.every(
      (field: string) =>
        office.billing?.[field as keyof typeof office.billing] ===
        billingForm.getFieldValue(field),
    );
    const submitShouldBeDisabled =
      hasEmptyRequired || (isUnchangedRequired && isUnchangedNonRequired);

    setFieldsChanged(valuesChanged);
    setIsSubmitDisabled(submitShouldBeDisabled);
  };

  const handleUpdateBilling = (
    newEmailFacturationAddress: ContactSort[],
    values?: Billing,
  ) => {
    setTempEmailFacturationAddress(newEmailFacturationAddress);
    let newBilling: Billing;
    if (values) {
      newBilling = values;
    } else if (office.billing) {
      newBilling = office.billing;
    } else {
      void requestUpdateBilling({
        dto: {
          officeId,
          emailFacturationAddress: newEmailFacturationAddress,
        },
      });
      return;
    }

    void requestUpdateBilling({
      dto: {
        officeName: newBilling.officeName,
        address1: newBilling.address1,
        address2: newBilling.address2,
        zipCode: newBilling.zipCode,
        city: newBilling.city,
        officeId,
        emailFacturationAddress: newEmailFacturationAddress,
      },
    });
  };

  const handleAddMe = (me?: ActiveUser) => {
    if (!me) {
      return;
    }

    const newEmailFacturationAddress = [...tempEmailFacturationAddress].filter(
      (recipient) => recipient.email !== me.email,
    );

    newEmailFacturationAddress.push({
      key: me.email + Date.now().toString(),
      firstname: me.firstname,
      lastname: me.lastname,
      email: me.email,
    });

    handleUpdateBilling(newEmailFacturationAddress, office.billing);
  };

  return (
    <>
      <h2>{t("offices.office.descriptionTitle")}</h2>
      <OfficeDescription office={office} />
      {showIsExpertComptable && (
        <>
          <h2
            className="pt-48"
            id={"billingAddress_" + office.id}
            ref={billingAddressRef}
          >
            {t("offices.office.billing")}
            <InfoModal value="billing" info />
          </h2>
          <Row gutter={32}>
            <Col span={24} sm={16}>
              <BillingForm
                className="pb-32"
                billingForm={billingForm}
                onBillingFormSubmit={handleBillingFormSubmit}
                onFieldsChange={onFieldsChange}
              />
              <Recipient
                office={office}
                isAddBillingEmail
                emailFacturationAddress={tempEmailFacturationAddress}
                onChangeEmailFacturationAddress={handleUpdateBilling}
                onAddMe={handleAddMe}
                contacts={contacts}
                members={members}
              />
            </Col>
            <Col
              span={24}
              sm={8}
              className="d-flex flex-column align-items-center justify-content-flex-start gap-24 mt-16"
            >
              {fieldsChanged && (
                <>
                  <BasicButton
                    text={t("offices.office.modify")}
                    onClick={billingForm.submit}
                    disabled={isSubmitDisabled}
                  />
                  <BasicButton
                    variant="tertiary"
                    text={t("offices.office.cancel")}
                    onClick={handleResetForm}
                  />
                </>
              )}
              {office.cgu && office.billing?.address1 && (
                <BasicLink
                  to={ROUTES.extranet.menu.offices.balanceAndStatistics.generate(
                    officeId,
                  )}
                  className="text-tertiary"
                  dataAlt={t("offices.office.credit")}
                >
                  {t("offices.office.credit")}
                </BasicLink>
              )}
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

export default OfficeCell;
